<template>
  <v-dialog :value="value" max-width="1100" scrollable persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="editor-header white--text">
        <v-icon color="white" class="mr-2">{{ isNew ? "mdi-plus-circle-outline" : "mdi-pencil" }}</v-icon>
        {{ heading }}
        <v-spacer></v-spacer>
        <v-chip v-if="sourceLabel" small class="mr-2">{{ sourceLabel }}</v-chip>
        <v-btn icon dark @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-row>
          <!-- ฟอร์ม -->
          <v-col cols="12" md="7">
            <v-alert v-if="sourceQuestions.length" type="info" text dense class="mb-4">
              <div class="font-weight-medium mb-1">คำถามจากผู้ใช้ที่จะตอบด้วยหัวข้อนี้</div>
              <div v-for="q in sourceQuestions" :key="q" class="d-flex align-center">
                <v-icon small :color="questionHits(q).length ? 'success' : 'error'" class="mr-1">
                  {{ questionHits(q).length ? "mdi-check-circle" : "mdi-alert-circle" }}
                </v-icon>
                <span>"{{ q }}"</span>
                <span class="caption grey--text ml-2">
                  {{ questionHits(q).length ? `ตรงกับคำสำคัญ: ${questionHits(q).join(", ")}` : "ยังไม่มีคำสำคัญที่ตรง" }}
                </span>
              </div>
            </v-alert>

            <v-form ref="form" v-model="valid">
              <v-text-field
                v-model.trim="form.title"
                label="หัวข้อ (แสดงเป็นปุ่มคำถาม) *"
                :rules="[v => !!v || 'กรุณากรอกหัวข้อ']"
                counter="150"
                maxlength="150"
                outlined
                dense
              ></v-text-field>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.category"
                    :items="categoryItems"
                    label="หมวด"
                    outlined
                    dense
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.roles"
                    :items="roleItems"
                    label="กลุ่มผู้ใช้ที่เกี่ยวข้อง"
                    hint="ว่าง = ทุกกลุ่ม · ถ้าระบุ คำตอบนี้จะได้น้ำหนักสูงขึ้นเมื่อกลุ่มนั้นถาม"
                    persistent-hint
                    multiple
                    small-chips
                    deletable-chips
                    outlined
                    dense
                  ></v-select>
                </v-col>
              </v-row>

              <v-combobox
                v-model="form.keywords"
                :search-input.sync="keywordSearch"
                label="คำสำคัญ (keyword) *"
                hint="พิมพ์แล้วกด Enter ทีละคำ · ใส่คำที่ผู้ใช้พิมพ์จริง คำพ้อง คำสะกดผิด · คำที่มีช่องว่าง = ต้องพบครบทุกคำ (ไม่สนลำดับ)"
                persistent-hint
                :rules="[v => (v && v.length > 0) || 'กรุณาเพิ่มคำสำคัญอย่างน้อย 1 คำ']"
                multiple
                small-chips
                deletable-chips
                append-icon=""
                outlined
                class="mt-3"
                @change="cleanKeywords"
              ></v-combobox>

              <v-textarea
                v-model="form.answer"
                label="คำตอบ *"
                hint="ใช้ **ข้อความ** เพื่อทำตัวหนา · Enter = ขึ้นบรรทัดใหม่"
                persistent-hint
                :rules="[v => !!(v && v.trim()) || 'กรุณากรอกคำตอบ']"
                counter="2000"
                maxlength="2000"
                rows="4"
                auto-grow
                outlined
                class="mt-3"
              ></v-textarea>

              <div class="section-label mt-2">
                ขั้นตอน (ไม่บังคับ)
                <v-btn x-small text color="primary" @click="form.steps.push('')">
                  <v-icon x-small left>mdi-plus</v-icon>เพิ่มขั้นตอน
                </v-btn>
              </div>
              <div v-for="(step, i) in form.steps" :key="`s${i}`" class="d-flex align-center">
                <span class="step-no">{{ i + 1 }}.</span>
                <v-text-field
                  v-model="form.steps[i]"
                  maxlength="300"
                  dense
                  outlined
                  hide-details
                  class="mb-2"
                ></v-text-field>
                <v-btn icon small :disabled="i === 0" @click="moveStep(i, -1)"><v-icon small>mdi-arrow-up</v-icon></v-btn>
                <v-btn icon small color="error" @click="form.steps.splice(i, 1)"><v-icon small>mdi-delete</v-icon></v-btn>
              </div>

              <v-text-field
                v-model.trim="form.tip"
                label="หมายเหตุ / ข้อควรระวัง (ไม่บังคับ)"
                maxlength="500"
                outlined
                dense
                class="mt-3"
              ></v-text-field>

              <div class="section-label">
                ปุ่มพาไปหน้าจอ (ไม่บังคับ)
                <v-btn
                  x-small
                  text
                  color="primary"
                  :disabled="form.links.length >= 8"
                  @click="form.links.push({ label: '', to: '', roles: [] })"
                >
                  <v-icon x-small left>mdi-plus</v-icon>เพิ่มปุ่ม
                </v-btn>
              </div>
              <v-row v-for="(link, i) in form.links" :key="`l${i}`" dense align="center">
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model.trim="link.label"
                    label="ข้อความบนปุ่ม"
                    :rules="[v => !!v || 'กรอกข้อความ']"
                    maxlength="60"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="5">
                  <v-autocomplete
                    v-model="link.to"
                    :items="routeItems"
                    label="หน้าจอในระบบ"
                    :rules="[v => !!v || 'เลือกหน้าจอ']"
                    dense
                    outlined
                  ></v-autocomplete>
                </v-col>
                <v-col cols="10" sm="3">
                  <v-select
                    v-model="link.roles"
                    :items="roleItems"
                    label="แสดงให้กลุ่ม"
                    placeholder="ทุกกลุ่ม"
                    multiple
                    small-chips
                    dense
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="2" sm="1" class="text-center">
                  <v-btn icon small color="error" class="mb-6" @click="form.links.splice(i, 1)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-row dense class="mt-2">
                <v-col cols="12" sm="6">
                  <v-switch
                    v-model="form.pinned"
                    inset
                    dense
                    label="ปักหมุดเป็นคำถามที่พบบ่อย"
                    hint="แสดงเป็นปุ่มแรกๆ ตอนเปิดแชท (ตามกลุ่มผู้ใช้ที่ระบุ)"
                    persistent-hint
                  ></v-switch>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-switch v-model="form.is_active" inset dense color="success" label="เปิดใช้งาน"></v-switch>
                </v-col>
              </v-row>
            </v-form>
          </v-col>

          <!-- ตัวอย่าง -->
          <v-col cols="12" md="5">
            <div class="preview-sticky">
              <div class="section-label mb-2">ตัวอย่างที่ผู้ใช้จะเห็น</div>
              <div class="preview-surface">
                <chat-answer-preview :intent="previewIntent"></chat-answer-preview>
              </div>
              <div class="mt-4">
                <div class="section-label">ทดสอบคำสำคัญ</div>
                <v-text-field
                  v-model="testQuestion"
                  placeholder="ลองพิมพ์คำถามแบบผู้ใช้..."
                  dense
                  outlined
                  hide-details
                  clearable
                ></v-text-field>
                <div v-if="testQuestion" class="caption mt-1">
                  <template v-if="questionHits(testQuestion).length">
                    <v-icon x-small color="success">mdi-check-circle</v-icon>
                    ตรงกับ: {{ questionHits(testQuestion).join(", ") }}
                  </template>
                  <template v-else>
                    <v-icon x-small color="error">mdi-alert-circle</v-icon> ไม่ตรงกับคำสำคัญใดเลย
                  </template>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <span v-if="error" class="error--text caption">{{ error }}</span>
        <v-spacer></v-spacer>
        <v-btn text @click="close">ยกเลิก</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!valid" @click="save">
          <v-icon left>mdi-content-save</v-icon>บันทึก
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * ChatbotFaqEditor.vue — ฟอร์มเพิ่ม/แก้ไขคำถาม-คำตอบของผู้ช่วย HRVEC (ใช้ในหน้า chatbot_manage.vue)
 *   intent.source = "builtin"  → หัวข้อที่มากับระบบ ยังไม่เคยแก้ → บันทึกเป็นแถวแก้ไขทับ (intent_key = id เดิม)
 *   intent.source = "override" → แก้ไขทับอยู่แล้ว → อัปเดตแถวเดิม
 *   intent.source = "custom"   → หัวข้อที่แอดมินเพิ่ม
 *   intent = null              → เพิ่มหัวข้อใหม่
 */
import { CATEGORIES, ROLE_LABELS } from "../knowledgeBase";
import { matchedKeywords } from "../chatEngine";
import ChatAnswerPreview from "./ChatAnswerPreview.vue";

const emptyForm = () => ({
  title: "",
  category: "general",
  roles: [],
  keywords: [],
  answer: "",
  steps: [],
  tip: "",
  links: [],
  pinned: false,
  is_active: true
});

export default {
  name: "ChatbotFaqEditor",
  components: { ChatAnswerPreview },
  props: {
    value: { type: Boolean, default: false },
    intent: { type: Object, default: null },
    sourceQuestions: { type: Array, default: () => [] },
    routeItems: { type: Array, default: () => [] }
  },
  data() {
    return {
      form: emptyForm(),
      valid: true,
      saving: false,
      error: "",
      keywordSearch: "",
      testQuestion: "",
      categoryItems: CATEGORIES.map(c => ({ text: c.label, value: c.id })),
      roleItems: Object.keys(ROLE_LABELS).map(k => ({ text: ROLE_LABELS[k], value: k }))
    };
  },
  computed: {
    isNew() {
      return !this.intent;
    },
    heading() {
      if (this.isNew) return "เพิ่มคำถาม-คำตอบใหม่";
      return this.intent.source === "builtin" ? "แก้ไขคำตอบในระบบ" : "แก้ไขคำถาม-คำตอบ";
    },
    sourceLabel() {
      if (!this.intent) return "";
      return { builtin: "มากับระบบ", override: "มากับระบบ (แก้ไขแล้ว)", custom: "เพิ่มโดยผู้ดูแลระบบ" }[this.intent.source];
    },
    previewIntent() {
      return { ...this.form };
    }
  },
  watch: {
    value(open) {
      if (open) this.reset();
    }
  },
  methods: {
    reset() {
      this.error = "";
      this.testQuestion = "";
      const it = this.intent;
      if (!it) {
        this.form = { ...emptyForm(), keywords: this.sourceQuestions.slice(0, 10) };
      } else {
        this.form = {
          title: it.title,
          category: it.category || "general",
          roles: [...(it.roles || [])],
          keywords: [...(it.keywords || []), ...this.sourceQuestions.filter(q => !(it.keywords || []).includes(q))],
          answer: it.answer || "",
          steps: [...(it.steps || [])],
          tip: it.tip || "",
          links: (it.links || []).map(l => ({ label: l.label, to: l.to, roles: [...(l.roles || [])] })),
          pinned: !!it.pinned,
          is_active: it.is_active !== false
        };
      }
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    cleanKeywords(list) {
      const seen = new Set();
      this.form.keywords = (list || [])
        .map(k => String(k).trim().slice(0, 60))
        .filter(k => k && !seen.has(k) && seen.add(k));
    },
    questionHits(question) {
      return matchedKeywords(this.form, question);
    },
    moveStep(i, dir) {
      const steps = this.form.steps;
      steps.splice(i + dir, 0, steps.splice(i, 1)[0]);
    },
    close() {
      this.$emit("input", false);
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.error = "";
      const it = this.intent;
      const payload = {
        action: "save",
        ...this.form,
        steps: this.form.steps.map(s => s.trim()).filter(Boolean),
        links: this.form.links.map(l => ({ label: l.label, to: l.to, roles: l.roles })),
        source_questions: this.sourceQuestions
      };
      if (it && it.faqId) payload.id = it.faqId;
      if (it && it.source !== "custom") payload.intent_key = it.id;
      try {
        const res = await this.$http.post("chatbot_faq.manage.php", payload);
        if (res.data && res.data.status) {
          this.$emit("saved", res.data);
          this.close();
        } else {
          this.error = (res.data && res.data.message) || "บันทึกไม่สำเร็จ";
        }
      } catch (e) {
        if (e.isSessionExpired) return;
        this.error = (e.response && e.response.data && e.response.data.message) || "บันทึกไม่สำเร็จ กรุณาลองใหม่";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.editor-header {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
}
.section-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #455a64;
}
.step-no {
  width: 24px;
  margin-bottom: 8px;
  color: #607d8b;
}
.preview-sticky {
  position: sticky;
  top: 0;
}
.preview-surface {
  padding: 16px;
  background: #f5f7fb;
  border-radius: 12px;
}
</style>
